import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { SvgCheveronLeft } from '@actual-app/components/icons/v1';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { Page } from '#components/Page';
import { useNavigate } from '#hooks/useNavigate';

import { useRecalculer } from './Recalculer';

export function Section({
  title,
  aside,
  children,
  style,
}: {
  title?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <View
      style={{
        backgroundColor: theme.tableBackground,
        border: `1px solid ${theme.tableBorder}`,
        borderRadius: 4,
        flexShrink: 0,
        ...style,
      }}
    >
      {title != null && (
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.tableBorder}`,
          }}
        >
          <Text style={{ fontSize: 15, fontWeight: 600 }}>{title}</Text>
          {aside != null && (
            <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
              {aside}
            </Text>
          )}
        </View>
      )}
      {children}
    </View>
  );
}

export function Line({
  lead,
  title,
  detail,
  detailColor,
  value,
  valueDetail,
  valueColor,
  last = false,
}: {
  lead?: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  detailColor?: string;
  value?: ReactNode;
  valueDetail?: ReactNode;
  valueColor?: string;
  last?: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        borderBottom: last ? undefined : `1px solid ${theme.tableBorder}`,
      }}
    >
      {lead != null && (
        <Text
          style={{
            minWidth: 24,
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 600,
            color: theme.pageTextSubdued,
          }}
        >
          {lead}
        </Text>
      )}
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontWeight: 500 }}>{title}</Text>
        {detail != null && (
          <Text
            style={{
              fontSize: 12,
              color: detailColor ?? theme.pageTextSubdued,
            }}
          >
            {detail}
          </Text>
        )}
      </View>
      {value != null && (
        // Never more than half the line: the title must keep room to read.
        <View
          style={{
            alignItems: 'flex-end',
            maxWidth: '50%',
            textAlign: 'right',
          }}
        >
          <Text style={{ fontWeight: 500, color: valueColor, ...styles.tnum }}>
            {value}
          </Text>
          {valueDetail != null && (
            <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
              {valueDetail}
            </Text>
          )}
        </View>
      )}
    </View>
  );
}

const dollars = new Intl.NumberFormat('fr-CA', {
  style: 'currency',
  currency: 'CAD',
  currencyDisplay: 'narrowSymbol',
});

/** Amounts as the mockups show them: « 1 220,31 $ ». */
export function useMoney() {
  return (cents: number) => dollars.format(cents / 100);
}

/**
 * A MTXbudget page. Its header bears the title, when everything was last
 * recalculated and the button that recalculates it all; on a phone, a
 * sub-page gets a back button.
 */
export function MtxPage({
  title,
  back,
  subtitle,
  children,
}: {
  title: string;
  back?: string;
  /** In place of the recalculation and its button, for a page of input. */
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  const { isNarrowWidth } = useResponsive();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const recalcul = useRecalculer();
  return (
    <Page
      header={
        <View
          style={{
            flexShrink: 0,
            paddingBottom: recalcul.below && subtitle === undefined ? 10 : 0,
            backgroundColor: theme.mobileHeaderBackground,
            borderBottom: `1px solid ${theme.tableBorder}`,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              padding: isNarrowWidth ? '10px 8px 10px 16px' : '10px 20px',
            }}
          >
            {isNarrowWidth && back && (
              <Button
                variant="bare"
                aria-label={t('Retour')}
                onPress={() => void navigate(back)}
                style={{
                  flexShrink: 0,
                  width: 44,
                  height: 44,
                  padding: 0,
                  margin: '0 -8px 0 -12px',
                }}
              >
                <SvgCheveronLeft width={24} height={24} />
              </Button>
            )}
            <View style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  lineHeight: 1.4,
                  color: theme.pageTextDark,
                }}
              >
                {title}
              </h1>
              {subtitle === undefined ? (
                recalcul.subtitle
              ) : (
                <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                  {subtitle}
                </Text>
              )}
            </View>
            {subtitle === undefined && recalcul.button}
          </View>
          {subtitle === undefined && recalcul.below}
        </View>
      }
    >
      {/* On a phone the navigation bar covers the bottom of the page: the
          content keeps its full height and ends above the bar. */}
      {isNarrowWidth ? (
        <View style={{ flexShrink: 0, paddingBottom: MOBILE_NAV_HEIGHT }}>
          {children}
        </View>
      ) : (
        children
      )}
    </Page>
  );
}

const dayMonth = new Intl.DateTimeFormat('fr-CA', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});
const dayMonthLong = new Intl.DateTimeFormat('fr-CA', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
});
const monthYear = new Intl.DateTimeFormat('fr-CA', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

export function shortDate(date: string) {
  return dayMonth.format(new Date(`${date}T00:00:00Z`));
}

const dayLongMonth = new Intl.DateTimeFormat('fr-CA', {
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
});

/** « 15 octobre » : a date inside a sentence, without an abbreviation dot. */
export function textDate(date: string) {
  return dayLongMonth.format(new Date(`${date}T00:00:00Z`));
}

export function longDate(date: string) {
  return dayMonthLong.format(new Date(`${date}T00:00:00Z`));
}

export function monthName(month: string) {
  return monthYear.format(new Date(`${month}-01T00:00:00Z`));
}

export function hoursLabel(hours: number) {
  return `${hours.toLocaleString('fr-CA', { maximumFractionDigits: 1 })} h`;
}
